// src/services/secure-store.service.ts
import * as SecureStore from "expo-secure-store";
import { Platform } from "react-native";
import { DecodedToken } from "../../../types/token";
import { DefaultLocation } from "../../../types/user";
import AuthCookieManager from "./AuthCookieManager";

export class SecureStoreService {
  private static readonly STORE_KEYS = {
    auth_token: "auth_token",
    refresh_token: "refresh_token",
    ph_verify: "ph_verify",
    default_location: "default_location",
  } as const;

  private decodedToken: DecodedToken | null = null;

  async setDecodedToken(token: DecodedToken): Promise<void> {
    this.decodedToken = token;
  }

  async getDecodedToken(): Promise<DecodedToken | null> {
    if (!this.decodedToken) {
      this.decodedToken = await this.getDecodedAccessToken();
    }
    return this.decodedToken;
  }

  // Access Token Operations
  async setAccessToken(token: string): Promise<void> {
    console.log(
      "[SecureStoreService] setAccessToken called with token:",
      token.substring(0, 10) + "..."
    );
    try {
      if (Platform.OS === "web") {
        AuthCookieManager.setAuthToken(token);
      } else {
        await SecureStore.setItemAsync(
          SecureStoreService.STORE_KEYS.auth_token,
          token
        );
      }
      console.log("[SecureStoreService] Access token stored successfully");
    } catch (error) {
      console.error(
        "[SecureStoreService] Failed to store access token:",
        error
      );
      throw new Error("Failed to store access token");
    }
  }

  async getDecodedAccessToken(): Promise<DecodedToken | null> {
    if (this.decodedToken) {
      return this.decodedToken;
    }
    console.log("[SecureStoreService] getDecodedAccessToken called");
    try {
      const token = await this.getAccessToken();
      if (!token) {
        console.log("[SecureStoreService] No access token found");
        return null;
      }

      const base64Payload = token.split(".")[1];
      const decodedPayload = decodeURIComponent(
        Array.prototype.map
          .call(
            atob(base64Payload.replace(/-/g, "+").replace(/_/g, "/")),
            (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
          )
          .join("")
      );

      const decodedToken = JSON.parse(decodedPayload) as DecodedToken;
      console.log(
        "[SecureStoreService] Access token decoded successfully",
        decodedToken
      );
      this.setDecodedToken(decodedToken);
      return decodedToken;
    } catch (error) {
      console.error(
        "[SecureStoreService] Failed to decode access token:",
        error
      );
      return null;
    }
  }

  async getAccessToken(): Promise<string | null> {
    console.log("[SecureStoreService] getAccessToken called");
    try {
      let token: string | null = null;
      if (Platform.OS === "web") {
        token = AuthCookieManager.getAuthToken();
      } else {
        token = await SecureStore.getItemAsync(
          SecureStoreService.STORE_KEYS.auth_token
        );
      }
      console.log(
        "[SecureStoreService] Access token retrieved:",
        token ? "Found" : "Not found"
      );
      return token;
    } catch (error) {
      console.error(
        "[SecureStoreService] Failed to retrieve access token:",
        error
      );
      return null;
    }
  }

  async deleteAccessToken(): Promise<void> {
    console.log("[SecureStoreService] deleteAccessToken called");
    try {
      if (Platform.OS === "web") {
        localStorage.removeItem(SecureStoreService.STORE_KEYS.auth_token);
      } else {
        await SecureStore.deleteItemAsync(
          SecureStoreService.STORE_KEYS.auth_token
        );
      }
      console.log("[SecureStoreService] Access token deleted successfully");
    } catch (error) {
      console.error(
        "[SecureStoreService] Failed to delete access token:",
        error
      );
      throw new Error("Failed to delete access token");
    }
  }

  // Refresh Token Operations
  async setRefreshToken(token: string): Promise<void> {
    console.log(
      "[SecureStoreService] setRefreshToken called with token:",
      token.substring(0, 10) + "..."
    );
    try {
      if (Platform.OS === "web") {
        AuthCookieManager.setRefreshToken(token);
      } else {
        await SecureStore.setItemAsync(
          SecureStoreService.STORE_KEYS.refresh_token,
          token
        );
      }
      console.log("[SecureStoreService] Refresh token stored successfully");
    } catch (error) {
      console.error(
        "[SecureStoreService] Failed to store refresh token:",
        error
      );
      throw new Error("Failed to store refresh token");
    }
  }

  async getRefreshToken(): Promise<string | null> {
    console.log("[SecureStoreService] getRefreshToken called");
    try {
      let token: string | null = null;
      if (Platform.OS === "web") {
        token = AuthCookieManager.getRefreshToken();
      } else {
        token = await SecureStore.getItemAsync(
          SecureStoreService.STORE_KEYS.refresh_token
        );
      }
      console.log(
        "[SecureStoreService] Refresh token retrieved:",
        token ? "Found" : "Not found"
      );
      return token;
    } catch (error) {
      console.error(
        "[SecureStoreService] Failed to retrieve refresh token:",
        error
      );
      return null;
    }
  }

  async deleteRefreshToken(): Promise<void> {
    console.log("[SecureStoreService] deleteRefreshToken called");
    try {
      if (Platform.OS === "web") {
        localStorage.removeItem(SecureStoreService.STORE_KEYS.refresh_token);
      } else {
        await SecureStore.deleteItemAsync(
          SecureStoreService.STORE_KEYS.refresh_token
        );
      }
      console.log("[SecureStoreService] Refresh token deleted successfully");
    } catch (error) {
      console.error(
        "[SecureStoreService] Failed to delete refresh token:",
        error
      );
      throw new Error("Failed to delete refresh token");
    }
  }

  // Phone Verification Status Operations
  async setPhoneVerificationStatus(status: string): Promise<void> {
    console.log(
      "[SecureStoreService] setPhoneVerificationStatus called with status:",
      status
    );
    try {
      if (Platform.OS === "web") {
        AuthCookieManager.setPhoneVerification(status);
      } else {
        await SecureStore.setItemAsync(
          SecureStoreService.STORE_KEYS.ph_verify,
          status
        );
      }
      console.log(
        "[SecureStoreService] Phone verification status stored successfully"
      );
    } catch (error) {
      console.error(
        "[SecureStoreService] Failed to store phone verification status:",
        error
      );
      throw new Error("Failed to store phone verification status");
    }
  }

  async getPhoneVerificationStatus(): Promise<string | null> {
    console.log("[SecureStoreService] getPhoneVerificationStatus called");
    try {
      let status: string | null = null;
      if (Platform.OS === "web") {
        status = AuthCookieManager.getPhoneVerification();
      } else {
        status = await SecureStore.getItemAsync(
          SecureStoreService.STORE_KEYS.ph_verify
        );
      }
      console.log(
        "[SecureStoreService] Phone verification status retrieved:",
        status ? "Found" : "Not found"
      );
      return status;
    } catch (error) {
      console.error(
        "[SecureStoreService] Failed to retrieve phone verification status:",
        error
      );
      return null;
    }
  }

  async deletePhoneVerificationStatus(): Promise<void> {
    console.log("[SecureStoreService] deletePhoneVerificationStatus called");
    try {
      if (Platform.OS === "web") {
        localStorage.removeItem(SecureStoreService.STORE_KEYS.ph_verify);
      } else {
        await SecureStore.deleteItemAsync(
          SecureStoreService.STORE_KEYS.ph_verify
        );
      }
      console.log(
        "[SecureStoreService] Phone verification status deleted successfully"
      );
    } catch (error) {
      console.error(
        "[SecureStoreService] Failed to delete phone verification status:",
        error
      );
      throw new Error("Failed to delete phone verification status");
    }
  }

  // Clear all stored data
  async clearAll(): Promise<void> {
    console.log("[SecureStoreService] clearAll called");
    try {
      this.decodedToken = null;
      if (Platform.OS === "web") {
        Object.values(SecureStoreService.STORE_KEYS).forEach((key) => {
          localStorage.removeItem(key);
        });
      } else {
        const deletePromises = Object.values(SecureStoreService.STORE_KEYS).map(
          (key) => SecureStore.deleteItemAsync(key)
        );
        await Promise.all(deletePromises);
      }
      console.log(
        "[SecureStoreService] All secure store data cleared successfully"
      );
    } catch (error) {
      console.error(
        "[SecureStoreService] Failed to clear all secure store data:",
        error
      );
      throw new Error("Failed to clear all secure store data");
    }
  }

  async setDefaultLocation(location: DefaultLocation): Promise<void> {
    try {
      const locationString = JSON.stringify(location);
      if (Platform.OS === "web") {
        localStorage.setItem(
          SecureStoreService.STORE_KEYS.default_location,
          locationString
        );
      } else {
        await SecureStore.setItemAsync(
          SecureStoreService.STORE_KEYS.default_location,
          locationString
        );
      }
      console.log("[SecureStoreService] Default location stored successfully");
    } catch (error) {
      console.error(
        "[SecureStoreService] Failed to store default location:",
        error
      );
      throw new Error("Failed to store default location");
    }
  }

  // Retrieve default location
  async getDefaultLocation(): Promise<DefaultLocation | null> {
    try {
      let locationString: string | null = null;
      if (Platform.OS === "web") {
        locationString = localStorage.getItem(
          SecureStoreService.STORE_KEYS.default_location
        );
      } else {
        locationString = await SecureStore.getItemAsync(
          SecureStoreService.STORE_KEYS.default_location
        );
      }
      if (!locationString) {
        console.log("[SecureStoreService] No default location found");
        return null;
      }
      const location = JSON.parse(locationString) as DefaultLocation;
      return location;
    } catch (error) {
      console.error(
        "[SecureStoreService] Failed to retrieve default location:",
        error
      );
      return null;
    }
  }
}

export const secureStoreService = new SecureStoreService();
